import React, { useEffect, useState } from 'react';
import moment from 'moment';
import 'moment/locale/es';
import { Patient } from '../../interfaces/Patients';
import { Doctor } from '../../interfaces/Doctors';
import { SurgicalNote } from '../../interfaces/SurgicalNote';
import { useMainProvider } from '../../context/MainProvider';
import { UserTypes } from '../../interfaces/User';
import { getDoctorFullName, getNurseFullName, getPatientFullName } from '../../utils/fns';
import { Nurse } from '../../interfaces/Nurses';

function SurgicalNoteDocument({
  patient,
  doctor,
  values,
}: {
  patient: Patient;
  doctor: Doctor;
  values: SurgicalNote;
}): JSX.Element {
  const { user } = useMainProvider();
  const [name, setName] = useState('');

  useEffect(() => {
    if (user && user.initialData) {
      if (user.typeId === UserTypes.PatientType) {
        setName(getPatientFullName(user.initialData as Patient).toUpperCase());
      }
      if (user.typeId === UserTypes.DoctorType) {
        setName(getDoctorFullName(user.initialData as Doctor).toUpperCase());
      }
      if (user.typeId === UserTypes.NurseType) {
        setName(getNurseFullName(user.initialData as Nurse).toUpperCase());
      }
    }
  }, [user]);

  useEffect(() => {
    moment.locale('es');
  }, [patient]);

  return (
    <table className='table-full-width'>
      <tbody>
        <tr>
          <td>
            <table className='table-full-width'>
              <tbody>
                <tr>
                  <td>
                    <span>
                      <p style={{ textAlign: 'end' }}>
                        <strong>
                          Fecha y Hora de impresión:{' '}
                          {moment().locale('ES').format('DD MMMM YYYY h:mm:ss a')}
                          <br />
                        </strong>
                      </p>

                      <p style={{ textAlign: 'right' }}>
                        <strong>
                          {doctor.med_consultorio?.toUpperCase()}
                          <br />
                          {doctor.med_raz_social?.toUpperCase()},{' '}
                          {doctor.med_no_registro?.toUpperCase()}
                          <br />
                          {doctor.med_domicilio?.toUpperCase()},
                          {doctor.med_colonia_consultorio?.toUpperCase()},{' '}
                          {doctor.med_municipio_consultorio?.toUpperCase()},{' '}
                          {doctor.med_ciudad_consultorio?.toUpperCase()},
                          {doctor.med_cp_consultorio}
                          <br />
                          Tel. {doctor.med_telefono_consultorio}
                        </strong>
                      </p>
                      <br />
                      <p style={{ textAlign: 'center', marginBottom: '50px' }}>
                        <strong>Nota Postoperatoria.</strong>
                      </p>

                      <p style={{ textAlign: 'start' }}>
                        <strong>
                          Fecha y Hora de elaboración de la Nota:{' '}
                          {moment(values.fecha)
                            .locale('ES')
                            .format('DD MMMM YYYY h:mm:ss a')}
                        </strong>
                      </p>

                      <p style={{ textAlign: 'start' }}>
                        <strong>
                          Datos del paciente
                          <br />
                        </strong>
                      </p>

                      <div className='print-data-container'>
                        <div className='print-data-left'>Nombre</div>

                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {patient.pac_nombre} {patient.pac_apellidop}{' '}
                            {patient.pac_apellidom}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'>Fecha de nacimiento</div>

                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {moment(patient.pac_fechanacimiento)
                              .locale('ES')
                              .format('DD/MM/YYYY')}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'>Edad</div>

                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {moment().diff(patient.pac_fechanacimiento, 'years')} años
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'>Sexo</div>

                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {patient.pac_sexo?.cat_sexo_desc}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'>Curp</div>

                        <div className='print-data-right'>
                          <span style={{ textTransform: 'uppercase' }}>
                            {patient.pac_curp}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'>Expediente</div>

                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {patient.pac_id}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Grupo Sanguíneo</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {patient.pac_tipo_sangre}
                          </span>
                        </div>
                      </div>

                      <p style={{ textAlign: 'start' }}>
                        <strong>
                          Datos de la nota
                          <br />
                        </strong>
                      </p>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Fecha y hora de creación</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {moment(values.fecha).locale('es').format('LLLL')}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Cirugía</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.tipo_cirugia}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Fecha de inicio</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {moment(values.fecha_cirugia).locale('es').format('ll')}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Hora de inicio</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {moment(values.hora_inicio).locale('es').format('HH:mm')}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Hora de término</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {moment(values.hora_fin).locale('es').format('HH:mm')}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Diagnóstico preoperatorio</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.diag_preoperatorio?.map((singleDiag) => {
                              return (
                                <p style={{ margin: '0', marginBottom: '15px' }}>
                                  - {singleDiag.cie_clave} {singleDiag.cie_descripcion}
                                </p>
                              );
                            })}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Operación planeada</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.cirugia_programada}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Operación realizada</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.cirugia_efectuada}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Diagnóstico postoperatorio</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.diag_postoperatorio?.map((singleDiag) => {
                              return (
                                <p style={{ margin: '0', marginBottom: '15px' }}>
                                  - {singleDiag.cie_clave} {singleDiag.cie_descripcion}
                                </p>
                              );
                            })}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'>
                          {' '}
                          Descripción de la técnica quirúrgica
                        </div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.tecnica}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'>
                          {' '}
                          Tuvo dificultad técnica durante el procedimiento quirúrgico
                        </div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.dificultad_cirugia ? 'Si' : 'No'}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Especifique</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.dificultad_cirugia_descripcion}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Hallazgos transoperatorios</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.hallazgos}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'>
                          {' '}
                          Cuenta de gasas y compresas completas
                        </div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.cuenta_gasas_compresas ? 'Si' : 'No'}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Quién informo</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.cuenta_gasas_compresas_informo}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Quién informo</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.cuenta_gasas_compresas_informo}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Incidentes y accidentes</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.complicaciones}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Cuantificación de sangrado</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.sangrado ? 'Si' : 'No'}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Cantidad aproximada</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.sangrado_cantidad}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'>
                          {' '}
                          Estudios de servicios auxiliares de diagnóstico transoperatorios
                        </div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.servicios_aux}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'>
                          {' '}
                          Envio de piezas a patología
                        </div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.envio_pat ? 'Si' : 'No'}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Descripción piezas</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.envio_pat_desc}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Nombre del cirujano</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.cirujano}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Nombre del primer ayudante</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.ayudante}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'>
                          {' '}
                          Nombre del segundo ayudante
                        </div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.segudo_ayudante}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Nombre del anestesiólogo</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.anestesiologo}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Nombre del instrumentista</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.instrumentista}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Nombre de circulante</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.circulante}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'>
                          {' '}
                          Estado posquirúrgico inmedianto
                        </div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.estado_post}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'>
                          {' '}
                          Plan de Manejo y Tratamiento Posquirúrgico Inmediato
                        </div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.pla_inm}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Pronóstico</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.pronostico}
                          </span>
                        </div>
                      </div>

                      <div className='print-data-container'>
                        <div className='print-data-left'> Otros hallazgos</div>
                        <div className='print-data-right'>
                          <span style={{ textTransform: 'capitalize' }}>
                            {values.otros_llagazgos}
                          </span>
                        </div>
                      </div>

                      <p style={{ textAlign: 'start' }}>
                        <strong>
                          Datos del doctor
                          <br />
                        </strong>
                      </p>
                    </span>

                    <div className='print-data-container'>
                      <div className='print-data-left'>Nombre</div>

                      <div className='print-data-right'>
                        <span style={{ textTransform: 'capitalize' }}>
                          {doctor.med_nombre} {doctor.med_apellidop}{' '}
                          {doctor.med_apellidom}
                        </span>
                      </div>
                    </div>

                    <div className='print-data-container'>
                      <div className='print-data-left'>Especialidad</div>

                      <div className='print-data-right'>
                        <span style={{ textTransform: 'capitalize' }}>
                          {doctor.med_especialidad?.cat_especialidad_desc}
                        </span>
                      </div>
                    </div>

                    <div className='print-data-container'>
                      <div className='print-data-left'>Cédula</div>

                      <div className='print-data-right'>
                        <span style={{ textTransform: 'capitalize' }}>
                          {doctor.med_cedula_profesional}
                        </span>
                      </div>
                    </div>

                    {doctor.med_especialidad_id !== 37 ? (
                      <>
                        <div className='print-data-container'>
                          <div className='print-data-left'>Cédula especialidad</div>

                          <div className='print-data-right'>
                            <span style={{ textTransform: 'capitalize' }}>
                              {doctor.med_cedula_especialidad}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : null}

                    <p style={{ textAlign: 'start' }}>
                      <strong>
                        Aviso de privacidad
                        <br />
                      </strong>
                    </p>
                    <p style={{ fontSize: '0.8rem' }}>
                      Para ver nuestro Aviso de privacidad favor consultar:
                      http://zivot.com.mx/aviso-privacidad
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export default SurgicalNoteDocument;
